import { createApi } from "@reduxjs/toolkit/query/react";
import endpoints, { baseURL as v1BaseUrl } from "data/api/v1/endpoints.ts";
import {
	APIErrorResponse,
	baseQueryWithRedirect,
	encodePostRequest,
	UntransformedResponse
} from "features/api/utils/apiUtils.ts";
import { SaveDraftRequestBody } from "types/api/orders/postDraftOrder.ts";
import { GetDraftOrdersResponse } from "types/api/orders/getDraftOrders.ts";
import { GetDraftByIdResponse } from "types/api/orders/getDraftById.ts";
import { GetOrderByIdResponse, GetOrderByIdResponseData } from "types/api/orders/getOrderById.ts";
import {
	PostReplacementOrderRequestBody,
	PostReplacementOrderResponse,
	PostReplacementOrderResponseData
} from "types/api/orders/postReplacementOrder.ts";
import { v4 as uuidv4 } from "uuid";
import { GetOrderSummaryResponse, GetOrderSummaryResponseData } from "types/api/orders/getOrderSummary.ts";
import { DateRange } from "pages/OrderPages/OrderListPage/OrderListPage";
import { baseURL, getOrdersInitial } from "constants/endpoints";
import { encodeQueryStringToBase64 } from "utils/string";
import { GetOrderResponse } from "types/api/orders/getOrder.ts";
import { SubmitDraftResponse } from "types/api/orders/submitDraftOrder.ts";
import { GetDraftExportById, GetDraftExportByIdParameters } from "types/api/orders/exportDraftQuoteById.ts";
import { GetLinkedOrdersResponse, GetLinkedOrdersResponseData } from "types/api/orders/getLinkedOrders.ts";

export interface OrderSummaryRequest {
	searchDates: DateRange;
	searchTerm: string;
}

export const formatSummaryPath = (req: OrderSummaryRequest): string => {
	let uri = `${baseURL}${getOrdersInitial}`;
	const queryParams: string[] = [];

	if (req.searchDates?.fromDate !== undefined) {
		const fromDateISO = req.searchDates?.fromDate.toISOString().split("T")[0];
		queryParams.push(`FromDate=${fromDateISO}`);
	}

	if (req.searchDates?.toDate !== undefined) {
		const toDateISO = req.searchDates?.toDate.toISOString().split("T")[0];
		queryParams.push(`ToDate=${toDateISO}`);
	}

	if (req.searchTerm !== undefined && req.searchTerm.trim() !== "") {
		queryParams.push(`Search=${encodeURIComponent(req.searchTerm)}`);
	}

	if (queryParams.length > 0) {
		uri += `?${queryParams.join("&")}`;
	}

	return encodeQueryStringToBase64(uri);
};

export const lineItemIdSymbol = Symbol("lineItemId");
export const lineItemConfigurationIdSymbol = Symbol("configurationId");

export const orderApiSlice = createApi({
	reducerPath: "orderApi",
	baseQuery: baseQueryWithRedirect(v1BaseUrl),
	endpoints: (builder) => ({
		getOrderNumbers: builder.query<string[], void>({
			query: () => endpoints.orders.getOrders(),
			transformResponse: (response: GetOrderResponse) => {
				return response?.data?.items?.map((order) => order.orderNumber ?? "") ?? [];
			},
			transformErrorResponse: (error: UntransformedResponse<APIErrorResponse>) => ({
				status: error?.status
			})
		}),
		getById: builder.query<GetOrderByIdResponseData, string>({
			query: (orderId: string) => endpoints.orders.getOrdersById(orderId),
			transformResponse: (response: GetOrderByIdResponse) => {
				if (!response.data) return;
				return {
					...response.data,
					configurations: response.data?.configurations?.map((config) => {
						const configurationId = uuidv4();

						return {
							...config,
							configurationId,
							lineItems: config.lineItems?.map((lineItem) => ({
								...lineItem,
								[lineItemConfigurationIdSymbol]: configurationId,
								[lineItemIdSymbol]: uuidv4()
							}))
						};
					})
				};
			},
			transformErrorResponse: (error: { status: string | number }) => ({
				status: error?.status
			})
		}),
		getLinkedOrders: builder.query<GetLinkedOrdersResponseData, string>({
			query: (orderId: string) => endpoints.orders.getLinkedOrders(orderId),
			transformResponse: (response: GetLinkedOrdersResponse) => response?.data ?? []
		}),
		filter: builder.query<GetOrderSummaryResponseData, OrderSummaryRequest>({
			query: (req: OrderSummaryRequest) => formatSummaryPath(req),
			transformResponse: (response: GetOrderSummaryResponse) => response?.data
		}),
		createReplacementOrder: builder.mutation<PostReplacementOrderResponseData, PostReplacementOrderRequestBody>({
			query: (replacementOrder) => ({
				url: endpoints.orders.postReplacementOrder(),
				method: "POST",
				dataType: "json",
				headers: {
					"Content-type": "application/json; charset=UTF-8"
				},
				body: encodePostRequest(replacementOrder)
			}),
			transformResponse: (response: PostReplacementOrderResponse) => response?.data,
			// TO DO ADDRESS BETTER TYPING AROUND ERROR RESPONSE
			transformErrorResponse: (error: any) => ({
				data: {
					errors: error?.data?.errors,
					status: error?.status
				}
			})
		}),
		saveDraftOrder: builder.mutation({
			query: (draftOrder: SaveDraftRequestBody) => ({
				url: endpoints.orders.draftOrders(),
				method: "POST",
				dataType: "json",
				headers: {
					"Content-type": "application/json; charset=UTF-8"
				},
				body: encodePostRequest(draftOrder)
			}),
			transformErrorResponse: (error: any) => ({
				status: error?.status,
				data: {
					errors: error?.data?.errors
				}
			})
		}),
		exportDraftQuote: builder.query<GetDraftExportById, GetDraftExportByIdParameters>({
			query: (params?: GetDraftExportByIdParameters) => ({
				url: endpoints.orders.exportDraftById(params?.draftId ?? ""),
				method: "GET",
				responseHandler: (response: Response) => (response.ok ? response.blob() : response.json())
			}),
			transformErrorResponse: (error: UntransformedResponse<APIErrorResponse>) => ({
				errors: error?.data?.errors,
				status: error?.status.toString()
			}),
			keepUnusedDataFor: 0
		}),
		submitDraftOrder: builder.mutation({
			query: (draftOrder: SaveDraftRequestBody) => ({
				url: endpoints.orders.submitDraftOrder(),
				method: "POST",
				dataType: "json",
				headers: {
					"Content-type": "application/json; charset=UTF-8"
				},
				body: encodePostRequest(draftOrder)
			}),
			transformResponse: (response: SubmitDraftResponse) => response?.data,
			transformErrorResponse: (error: any) => ({
				status: error?.status,
				data: {
					errors: error?.data?.errors
				}
			})
		}),
		getDraftOrders: builder.query({
			query: () => endpoints.orders.draftOrders(),
			transformResponse: (response: GetDraftOrdersResponse) => response,
			transformErrorResponse: (error: UntransformedResponse<APIErrorResponse>) => ({
				status: error?.status
			})
		}),
		getTemplates: builder.query({
			query: () => endpoints.orders.getTemplates(),
			transformResponse: (response: GetDraftOrdersResponse) => response,
			transformErrorResponse: (error: UntransformedResponse<APIErrorResponse>) => ({
				status: error?.status
			})
		}),
		getQuotes: builder.query({
			query: () => endpoints.orders.getQuotes(),
			transformResponse: (response: GetDraftOrdersResponse) => response,
			transformErrorResponse: (error: UntransformedResponse<APIErrorResponse>) => ({
				status: error?.status
			})
		}),
		getDraftOrder: builder.query({
			query: (draftId: string) => endpoints.orders.draftById(draftId),
			transformResponse: (response: GetDraftByIdResponse) => response,
			transformErrorResponse: (error: UntransformedResponse<APIErrorResponse>) => ({
				status: error?.status
			})
		}),
		deleteDraftOrder: builder.mutation({
			query: (draftId: string) => ({
				url: endpoints.orders.draftById(draftId),
				method: "DELETE"
			})
		}),
		deleteTemplateOrder: builder.mutation({
			query: (templateId: string) => ({
				url: endpoints.orders.templateById(templateId),
				method: "DELETE"
			})
		})
	})
});

export const {
	useGetByIdQuery,
	useCreateReplacementOrderMutation,
	useLazyFilterQuery,
	useGetLinkedOrdersQuery,
	useSaveDraftOrderMutation,
	useSubmitDraftOrderMutation,
	useLazyGetDraftOrdersQuery,
	useLazyGetTemplatesQuery,
	useLazyGetQuotesQuery,
	useDeleteDraftOrderMutation,
	useDeleteTemplateOrderMutation,
	useLazyGetDraftOrderQuery,
	useLazyExportDraftQuoteQuery,
	useGetOrderNumbersQuery
} = orderApiSlice;
